import React, { useState, useCallback, useEffect, Suspense } from "react";
import { Loader } from "../Loader";
import { DateRangePicker } from "../Filters";
import { MainMonthlyOverview } from "./MainMonthlyOverview";
import { ClientOverview } from "./ClientOverview";
import { MonthlyAnalysis } from "./MonthlyAnalysis";
import { ChurnAnalysis } from "./ChurnAnalysis";
import { EngagementUsage } from "./EngagementUsage";
import { TransferPatterns } from "./TransferPatterns";
const BehavioralAnalytics = React.lazy(() => import("./BehavioralAnalytics"));
import DaysToFirstTransferAnalysis from "./DaysToFirstTransferAnalysis";
import NeverTransferProbability from "./NeverTransferProbability";
import RegistrationPatternAnalysis from "./RegistrationPatternAnalysis";
import { UserSegment } from "./UserSegment";
import { Q2KPI } from "./Q2KPI";
import MondayBoards from "./MondayBoards";
import WorkforceAnalysis from "./WorkforceAnalysis";
import { KpiQueryParams, CompanyInfo } from "../../types/kpi.types";
import { analyticsService } from "../../services/analytics.service";
import { CompanyCombobox } from "../Filters/CompanyCombobox";
import { format } from "date-fns";

interface DashboardProps {
  onLogout: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [queryParams, setQueryParams] = useState<KpiQueryParams>({
    startDate: "2022-01-01",
    endDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [activeSection, setActiveSection] = useState<string>("q2-kpi");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("all");

  // Fetch logo and companies on mount
  useEffect(() => {
    analyticsService
      .getPayroLogo()
      .then((logo) => {
        console.log("Logo URL:", logo);
        setLogoUrl(logo);
      })
      .catch((err) => {
        console.error("Logo fetch error:", err);
        setLogoUrl(null);
      });

    analyticsService
      .getCompanies(queryParams.database)
      .then((companiesList) => {
        setCompanies(companiesList);
      })
      .catch((err) => {
        console.error("Error loading companies:", err);
      });
  }, [queryParams.database]);

  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    console.log("=== Dashboard handleDateChange ===");
    console.log("New startDate:", startDate);
    console.log("New endDate:", endDate);
    setQueryParams((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  }, []);

  const handleCompanyChange = useCallback((companyId: string) => {
    setSelectedCompany(companyId);
    setQueryParams((prev) => ({
      ...prev,
      companyId: companyId === "all" ? undefined : companyId,
    }));
  }, []);

  const sections = [
    { id: "q2-kpi", label: "🎯 Q2 KPI" },
    { id: "monday-boards", label: "📋 Monday Boards" },
    { id: "main-monthly", label: "Main Monthly Overview" },
    { id: "overview", label: "Client Overview" },
    { id: "monthly", label: "📅 Monthly Transfer Activity" },
    { id: "churn", label: "📉 Turnover Analysis" },
    { id: "workforce", label: "🏭 Workforce Analysis" },
    { id: "engagement", label: "Engagement & Usage" },
    { id: "transfers", label: "Transfer Patterns" },
    { id: "behavioral", label: "Behavioral Analytics" },
    { id: "days-to-first-transfer", label: "Days to First Transfer Analysis" },
    { id: "never-transfer-probability", label: "Never Transfer Probability" },
    { id: "registration-pattern", label: "Registration Pattern Analysis" },
    { id: "user-segment", label: "User Segment" },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "q2-kpi":
        return <Q2KPI database={queryParams.database} />;
      case "main-monthly":
        return <MainMonthlyOverview queryParams={queryParams} />;
      case "overview":
        return <ClientOverview queryParams={queryParams} />;
      case "monthly":
        return <MonthlyAnalysis queryParams={queryParams} />;
      case "churn":
        return <ChurnAnalysis queryParams={queryParams} />;
      case "workforce":
        return (
          <WorkforceAnalysis
            queryParams={queryParams}
            companyName={companies.find((c) => c.id.toString() === selectedCompany)?.name}
          />
        );
      case "engagement":
        return <EngagementUsage queryParams={queryParams} />;
      case "transfers":
        return <TransferPatterns queryParams={queryParams} />;
      case "behavioral":
        return (
          <Suspense fallback={<Loader />}>
            <BehavioralAnalytics queryParams={queryParams} />
          </Suspense>
        );
      case "days-to-first-transfer":
        return <DaysToFirstTransferAnalysis queryParams={queryParams} />;
      case "never-transfer-probability":
        return <NeverTransferProbability queryParams={queryParams} />;
      case "registration-pattern":
        return <RegistrationPatternAnalysis queryParams={queryParams} />;
      case "user-segment":
        return <UserSegment queryParams={queryParams} />;
      case "monday-boards":
        return <MondayBoards />;
      default:
        return <ClientOverview queryParams={queryParams} />;
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <header
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "20px",
          position: "relative",
        }}
      >
        {/* Top row: title left, logo + sign out right */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h1 style={{ margin: 0, color: "#333" }}>Payro KPI Dashboard</h1>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt='Payro'
                style={{ width: "80px", height: "80px", objectFit: "contain" }}
              />
            )}
            <button
              onClick={onLogout}
              style={{
                padding: "8px 16px",
                background: "#1a1a2e",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Company Selector */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginBottom: "12px",
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontWeight: 600, color: "#333" }}>Company:</label>
          <CompanyCombobox
            companies={companies}
            value={selectedCompany}
            onChange={handleCompanyChange}
          />
        </div>

        <DateRangePicker onDateChange={handleDateChange} />
      </header>

      <nav
        style={{
          backgroundColor: "#fff",
          padding: "0 20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0",
            borderBottom: "2px solid #e0e0e0",
          }}
        >
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              style={{
                padding: "16px 24px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                borderBottom:
                  activeSection === section.id
                    ? "3px solid #4CAF50"
                    : "3px solid transparent",
                color: activeSection === section.id ? "#4CAF50" : "#666",
                fontWeight: activeSection === section.id ? "bold" : "normal",
                transition: "all 0.2s",
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </nav>

      <main style={{ padding: "0 20px 40px 20px" }}>{renderSection()}</main>
    </div>
  );
};
